import React, { useEffect, useState, useMemo } from 'react';
import { Container, Button, Form, Spinner, ListGroup, Badge, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import Layout from '../../../components/ui/Layout';
import { useNotification } from '../../../components/ui/Notification';
import { createLogger } from '../../../utils/logger';
import {
  buildInventoryPricingFields,
  serializeTypeDiscountDeals,
  hasTypeSpecificQuantities,
} from '../../../utils/inventoryPricing';

const logger = createLogger('EditInventoryItem');

const EditInventoryItem = () => {
  const { itemId } = useParams();
  const { currentUser, userRole, employeeSupermarketId } = useAuth();
  const { showNotification } = useNotification();
  const [supermarkets, setSupermarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupermarketId, setSelectedSupermarketId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState(null);
  
  // Discount deals state
  const [discountDeals, setDiscountDeals] = useState([]);
  const [dealQuantity, setDealQuantity] = useState('');
  const [dealPrice, setDealPrice] = useState('');
  const [typeSelections, setTypeSelections] = useState({});
  const [typePriceInputs, setTypePriceInputs] = useState({});
  const [typeQuantityInputs, setTypeQuantityInputs] = useState({});
  const [typeDealsRecord, setTypeDealsRecord] = useState({});
  const [typeDealDrafts, setTypeDealDrafts] = useState({});
  
  const navigate = useNavigate();

  const isEmployeeLimited = userRole === 'Employee' && employeeSupermarketId;
  const canEdit = userRole === 'Admin' || (isEmployeeLimited && selectedSupermarketId === employeeSupermarketId);

  const productHasTypes = !!(selectedProductData?.product_types && selectedProductData.product_types.length > 0);

  const getFormBaselineUnitPrice = () => {
    const types = selectedProductData?.product_types || [];
    if (!types.length) {
      const sp = parseFloat(salePrice);
      return !Number.isNaN(sp) && sp > 0 ? sp : null;
    }
    const prices = [];
    types.forEach((t) => {
      if (t.id && typeSelections[t.id]) {
        const p = parseFloat(typePriceInputs[t.id]);
        if (!Number.isNaN(p) && p > 0) prices.push(p);
      }
    });
    return prices.length ? Math.min(...prices) : null;
  };

  const unitPriceForType = (typeId) => {
    const p = parseFloat(typePriceInputs[typeId]);
    return !Number.isNaN(p) && p > 0 ? p : null;
  };

  const getTypeDealDraft = (typeId) => typeDealDrafts[typeId] || { q: '', p: '' };

  const setTypeDealDraftField = (typeId, field, value) => {
    setTypeDealDrafts((prev) => ({
      ...prev,
      [typeId]: { q: '', p: '', ...prev[typeId], [field]: value },
    }));
  };

  // Filter and paginate products based on search term
  const filteredProducts = useMemo(() => {
    let filteredList = products;

    // Apply search filter
    if (productSearchTerm.trim()) {
      filteredList = products.filter(product => 
        product.display_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(productSearchTerm.toLowerCase()))
      );
    }

    // Limit results for performance
    return filteredList.slice(0, 15);
  }, [products, productSearchTerm]);

  const handleProductSelect = (product) => {
    setSelectedProductData(product);
    setSelectedProduct(product.id);
    setProductSearchTerm(product.display_name);
    setShowProductDropdown(false);
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    setShowProductDropdown(true);
    if (!value.trim()) {
      setSelectedProductData(null);
      setSelectedProduct('');
    }
  };

  // Discount deals handlers
  const addDiscountDeal = () => {
    if (!dealQuantity || !dealPrice) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter both quantity and price for the discount deal.',
        duration: 5000
      });
      return;
    }

    const quantityNum = parseInt(dealQuantity);
    const priceNum = parseFloat(dealPrice);

    if (isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({
        type: 'error',
        title: 'Invalid Quantity',
        message: 'Please enter a valid positive quantity.',
        duration: 5000
      });
      return;
    }

    if (isNaN(priceNum) || priceNum <= 0) {
      showNotification({
        type: 'error',
        title: 'Invalid Price',
        message: 'Please enter a valid positive price.',
        duration: 5000
      });
      return;
    }

    // Check if deal with same quantity already exists
    if (discountDeals.some(deal => deal.quantity === quantityNum)) {
      showNotification({
        type: 'error',
        title: 'Duplicate Deal',
        message: 'A discount deal with this quantity already exists.',
        duration: 5000
      });
      return;
    }

    const baselineUnit = getFormBaselineUnitPrice();
    if (baselineUnit == null) {
      showNotification({
        type: 'error',
        title: 'Set prices first',
        message: 'Enter a valid sale price before adding a deal.',
        duration: 5000
      });
      return;
    }
    if (priceNum >= baselineUnit * quantityNum) {
      showNotification({
        type: 'error',
        title: 'Invalid Deal Price',
        message: 'Deal price should be less than the regular price for the same quantity.',
        duration: 5000
      });
      return;
    }

    const newDeal = {
      id: Date.now(), // Simple ID for local state management
      quantity: quantityNum,
      price: priceNum
    };

    const updatedDeals = [...discountDeals, newDeal];
    logger.debug('[EditInventoryItem] Adding new deal:', newDeal);
    logger.debug('[EditInventoryItem] Updated deals array:', updatedDeals);
    
    setDiscountDeals(updatedDeals);
    setDealQuantity('');
    setDealPrice('');
    
    showNotification({
      type: 'success',
      title: 'Deal Added',
      message: `Discount deal added: ${quantityNum} items for $${priceNum.toFixed(2)}`,
      duration: 3000
    });
  };

  const removeDiscountDeal = (dealId) => {
    const dealToRemove = discountDeals.find(deal => deal.id === dealId);
    setDiscountDeals(discountDeals.filter(deal => deal.id !== dealId));
    
    if (dealToRemove) {
      showNotification({
        type: 'info',
        title: 'Deal Removed',
        message: `Discount deal removed: ${dealToRemove.quantity} items for $${dealToRemove.price.toFixed(2)}`,
        duration: 3000
      });
    }
  };

  const addDiscountDealForType = (typeId) => {
    const { q, p } = getTypeDealDraft(typeId);
    if (!q || !p) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Enter deal quantity and price for this type.',
        duration: 5000
      });
      return;
    }
    const quantityNum = parseInt(q, 10);
    const priceNum = parseFloat(p);
    if (Number.isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({ type: 'error', title: 'Invalid Quantity', message: 'Enter a valid deal quantity.', duration: 5000 });
      return;
    }
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      showNotification({ type: 'error', title: 'Invalid Price', message: 'Enter a valid deal price.', duration: 5000 });
      return;
    }
    const unit = unitPriceForType(typeId);
    if (unit == null) {
      showNotification({ type: 'error', title: 'Set price first', message: 'Enter this type\'s unit price before adding deals.', duration: 5000 });
      return;
    }
    const existing = typeDealsRecord[typeId] || [];
    if (existing.some((d) => d.quantity === quantityNum)) {
      showNotification({ type: 'error', title: 'Duplicate Deal', message: 'A deal with this quantity already exists for this type.', duration: 5000 });
      return;
    }
    if (priceNum >= unit * quantityNum) {
      showNotification({
        type: 'error',
        title: 'Invalid Deal Price',
        message: 'Deal total should be less than the regular price for that many items.',
        duration: 5000
      });
      return;
    }
    const newDeal = { id: Date.now() + Math.random(), quantity: quantityNum, price: priceNum };
    setTypeDealsRecord((prev) => ({ ...prev, [typeId]: [...(prev[typeId] || []), newDeal] }));
    setTypeDealDrafts((prev) => ({ ...prev, [typeId]: { q: '', p: '' } }));
    showNotification({
      type: 'success',
      title: 'Deal Added',
      message: `Deal for this type: ${quantityNum} items for $${priceNum.toFixed(2)}`,
      duration: 3000
    });
  };

  const removeDiscountDealForType = (typeId, dealId) => {
    setTypeDealsRecord((prev) => ({
      ...prev,
      [typeId]: (prev[typeId] || []).filter((d) => d.id !== dealId),
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    const fetchDropdownDataAndItem = async () => {
      setPageLoading(true);
      try {
        // Fetch supermarkets
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        let supermarketsList = supermarketsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (isEmployeeLimited) {
          supermarketsList = supermarketsList.filter(s => s.id === employeeSupermarketId);
        }
        setSupermarkets(supermarketsList);

        // Fetch products
        const productsSnapshot = await getDocs(collection(db, 'Products'));
        const productsList = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsList);

        // Fetch the inventory item
        if (!itemId) {
          showNotification({
            type: 'error',
            title: 'Missing Item ID',
            message: 'Inventory item ID is missing.',
            duration: 5000
          });
          setPageLoading(false);
          return;
        }
        const itemDoc = await getDoc(doc(db, 'Inventory', itemId));
        if (itemDoc.exists()) {
          const itemData = itemDoc.data();
          
          // Check if user has permission to edit this item
          if (isEmployeeLimited && itemData.supermarket_id !== employeeSupermarketId) {
            showNotification({
              type: 'error',
              title: 'Access Denied',
              message: "You don't have permission to edit this inventory item.",
              duration: 5000
            });
            setPageLoading(false);
            return;
          }

          setSelectedSupermarketId(itemData.supermarket_id || '');
          setSelectedProduct(itemData.product_id || '');
          setSalePrice((itemData.sale_price || 0).toString());
          setQuantity((itemData.quantity || 0).toString());

          // Load existing discount deals
          logger.debug('[EditInventoryItem] Loading item data:', itemData);
          logger.debug('[EditInventoryItem] Raw discount_deals from DB:', itemData.discount_deals);
          
          // Set product search term and data for display
          const productData = productsList.find(p => p.id === itemData.product_id);
          const types = productData?.product_types || [];
          const tp = itemData.type_prices || {};
          const hasPerTypeQty = hasTypeSpecificQuantities(itemData);

          if (types.length > 0) {
            setDiscountDeals([]);
            const sel = {};
            const inputs = {};
            const qtyInputs = {};
            types.forEach((t) => {
              if (!t.id) return;
              const has = tp[t.id] != null && Number(tp[t.id]) > 0;
              sel[t.id] = has;
              inputs[t.id] = has ? String(tp[t.id]) : '';
              if (has) {
                if (hasPerTypeQty) {
                  const tq = itemData.type_quantities || {};
                  qtyInputs[t.id] = String(parseInt(tq[t.id], 10) >= 0 ? tq[t.id] : 0);
                } else {
                  const pricedIds = types.filter((x) => x.id && tp[x.id] != null && Number(tp[x.id]) > 0).map((x) => x.id);
                  if (pricedIds[0] === t.id) {
                    qtyInputs[t.id] = String(itemData.quantity ?? 0);
                  } else {
                    qtyInputs[t.id] = '0';
                  }
                }
              }
            });
            setTypeSelections(sel);
            setTypePriceInputs(inputs);
            setTypeQuantityInputs(qtyInputs);

            const tdd = itemData.type_discount_deals || {};
            const dealsRec = {};
            types.forEach((t) => {
              if (!t.id || !sel[t.id]) return;
              const arr = tdd[t.id];
              if (Array.isArray(arr) && arr.length > 0) {
                dealsRec[t.id] = arr.map((d, index) => ({
                  id: Date.now() + index + Math.random(),
                  quantity: d.quantity,
                  price: d.price,
                }));
              }
            });
            if (Object.keys(dealsRec).length === 0 && itemData.discount_deals && Array.isArray(itemData.discount_deals) && itemData.discount_deals.length > 0) {
              const firstId = types.find((t) => t.id && sel[t.id])?.id;
              if (firstId) {
                dealsRec[firstId] = itemData.discount_deals.map((deal, index) => ({
                  id: Date.now() + index,
                  quantity: deal.quantity,
                  price: deal.price,
                }));
              }
            }
            setTypeDealsRecord(dealsRec);
            setTypeDealDrafts({});
          } else {
            setTypeSelections({});
            setTypePriceInputs({});
            setTypeQuantityInputs({});
            setTypeDealsRecord({});
            setTypeDealDrafts({});
            if (itemData.discount_deals && Array.isArray(itemData.discount_deals)) {
              const dealsWithIds = itemData.discount_deals.map((deal, index) => ({
                id: Date.now() + index,
                quantity: deal.quantity,
                price: deal.price
              }));
              setDiscountDeals(dealsWithIds);
            } else {
              setDiscountDeals([]);
            }
          }

          if (productData) {
            setProductSearchTerm(productData.display_name || '');
            setSelectedProductData(productData);
          }
        } else {
          showNotification({
            type: 'error',
            title: 'Item Not Found',
            message: 'Inventory item not found.',
            duration: 5000
          });
        }

      } catch (err) {
        showNotification({
          type: 'error',
          title: 'Fetch Error',
          message: `Failed to fetch data. ${  err.message}`,
          duration: 5000
        });
        logger.error(err);
      }
      setPageLoading(false);
    };

    if (currentUser) {
      fetchDropdownDataAndItem();
    }
    // Intentionally omit showNotification: unstable refs previously re-ran this effect on every toast and reset form state.
  }, [currentUser, userRole, employeeSupermarketId, isEmployeeLimited, itemId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupermarketId || !selectedProduct) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields.',
        duration: 5000
      });
      return;
    }
    if (!productHasTypes && !quantity) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter quantity.',
        duration: 5000
      });
      return;
    }
    if (!productHasTypes && !salePrice) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter a sale price.',
        duration: 5000
      });
      return;
    }

    if (userRole === 'Employee' && !employeeSupermarketId) {
      showNotification({
        type: 'error',
        title: 'Access Error',
        message: "Cannot edit inventory: You are not assigned to a supermarket.",
        duration: 5000
      });
      return;
    }

    if (isEmployeeLimited && selectedSupermarketId !== employeeSupermarketId) {
      showNotification({
        type: 'error',
        title: 'Permission Error',
        message: "Error: You can only edit inventory for your assigned supermarket.",
        duration: 5000
      });
      return;
    }

    setSubmitting(true);
    try {
      let quantityNum;
      let type_quantities = {};
      let type_discount_deals = {};
      let salePriceNum;
      let type_prices = {};

      if (productHasTypes) {
        const rawPrices = {};
        selectedProductData.product_types.forEach((t) => {
          if (!t.id || !typeSelections[t.id]) return;
          const p = parseFloat(typePriceInputs[t.id]);
          if (Number.isNaN(p) || p <= 0) {
            throw new Error(`Enter a valid price for type "${t.name || t.id}".`);
          }
          rawPrices[t.id] = p;
          const q = parseInt(typeQuantityInputs[t.id], 10);
          if (Number.isNaN(q) || q < 0) {
            throw new Error(`Enter a valid quantity (0 or more) for type "${t.name || t.id}".`);
          }
          type_quantities[t.id] = q;
        });
        if (Object.keys(rawPrices).length === 0) {
          throw new Error('Select at least one product type and enter its price and quantity.');
        }
        const built = buildInventoryPricingFields(rawPrices, null);
        salePriceNum = built.sale_price;
        type_prices = built.type_prices;
        quantityNum = Object.values(type_quantities).reduce((a, b) => a + b, 0);
        type_discount_deals = serializeTypeDiscountDeals(typeDealsRecord);
      } else {
        quantityNum = parseInt(quantity, 10);
        if (Number.isNaN(quantityNum) || quantityNum <= 0) {
          throw new Error('Invalid quantity.');
        }
        const sp = parseFloat(salePrice);
        if (Number.isNaN(sp) || sp <= 0) {
          throw new Error('Invalid sale price.');
        }
        const built = buildInventoryPricingFields({}, sp);
        salePriceNum = built.sale_price;
        type_prices = built.type_prices;
      }

      const dealsToSave = !productHasTypes && discountDeals.length > 0
        ? discountDeals.map((deal) => ({ quantity: deal.quantity, price: deal.price }))
        : [];
      
      logger.debug('[EditInventoryItem] Saving discount deals:', dealsToSave);
      
      await updateDoc(doc(db, 'Inventory', itemId), {
        supermarket_id: selectedSupermarketId,
        product_id: selectedProduct,
        sale_price: salePriceNum,
        type_prices,
        quantity: quantityNum,
        ...(productHasTypes
          ? {
              type_quantities,
              type_discount_deals,
              discount_deals: [],
            }
          : {
              discount_deals: dealsToSave,
            }),
        updated_by_uid: currentUser?.uid,
        updated_by_email: currentUser?.email || 'Unknown User',
        updated_at: new Date(),
      });

      showNotification({
        type: 'success',
        title: 'Update Successful',
        message: `Inventory item updated successfully! ${dealsToSave.length} discount deal(s) saved.`,
        duration: 5000
      });
      
      // Refresh the data to ensure deals are properly loaded
      logger.debug('[EditInventoryItem] Refreshing data after successful update...');
      const refreshedItemDoc = await getDoc(doc(db, 'Inventory', itemId));
      if (refreshedItemDoc.exists()) {
        const refreshedItemData = refreshedItemDoc.data();
        logger.debug('[EditInventoryItem] Refreshed item data:', refreshedItemData);
        logger.debug('[EditInventoryItem] Refreshed discount_deals:', refreshedItemData.discount_deals);
      }
    } catch (err) {
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: `Failed to update inventory item. ${  err.message}`,
        duration: 5000
      });
      logger.error(err);
    }
    setSubmitting(false);
  };

  const handleGoBack = () => navigate(-1);

  if (pageLoading && !currentUser) {
    return (
      <Layout>
        <Container className="px-3">
          <p>Loading user data...</p>
        </Container>
      </Layout>
    );
  }

  if (userRole === 'Employee' && !employeeSupermarketId && !pageLoading) {
    return (
      <Layout>
        <Container className="mt-3 px-3">
          <Button variant="outline-secondary" onClick={handleGoBack} className="mb-3">&larr; Go Back</Button>
          <div className="alert alert-danger" role="alert">
            <strong>Access Restricted</strong><br />
            You are not assigned to any supermarket. Please contact an administrator to edit inventory.
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="px-3">
        <Button variant="outline-secondary" onClick={handleGoBack} className="mt-3 mb-3">
          &larr; Go Back
        </Button>
        <h1 className="mb-3 text-break">Edit Inventory Item</h1>

        {pageLoading ? (
          <div className="text-center">
            <Spinner animation="border" className="mb-3" />
            <p>Loading form data...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="supermarketSelect" className="mb-3">
              <Form.Label>Hardware store</Form.Label>
              <Form.Control
                as="select"
                value={selectedSupermarketId}
                onChange={(e) => setSelectedSupermarketId(e.target.value)}
                required
                disabled={isEmployeeLimited || submitting}
                className="w-100"
                style={{ fontSize: '16px' }}
              >
                {supermarkets.map((supermarket) => (
                  <option key={supermarket.id} value={supermarket.id}>
                    {supermarket.display_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="productSelect" className="mb-3">
              <Form.Label>Product</Form.Label>
              <div className="position-relative product-search-container">
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search for a product..."
                    value={productSearchTerm}
                    onChange={handleProductSearchChange}
                    onFocus={() => setShowProductDropdown(true)}
                    required
                    disabled={submitting || !canEdit}
                    className="w-100"
                    style={{ fontSize: '16px' }} // Prevents zoom on iOS
                  />
                  {selectedProductData && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setSelectedProductData(null);
                        setSelectedProduct('');
                        setProductSearchTerm('');
                      }}
                      style={{ 
                        fontSize: '16px', 
                        minWidth: '44px',
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        borderTopLeftRadius: '0',
                        borderBottomLeftRadius: '0',
                        zIndex: 10
                      }}
                    >
                      ×
                    </Button>
                  )}
                </div>
                
                {showProductDropdown && (
                  <div 
                    className="position-absolute w-100 bg-white border rounded-bottom shadow-sm"
                    style={{ 
                      zIndex: 1000, 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      top: '100%',
                      maxWidth: '100vw'
                    }}
                  >
                    {filteredProducts.length > 0 ? (
                      <ListGroup variant="flush">
                        {filteredProducts.map((product) => (
                          <ListGroup.Item
                            key={product.id}
                            action
                            onClick={() => handleProductSelect(product)}
                            className="cursor-pointer p-3"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                              <div className="flex-grow-1 w-100">
                                <div className="fw-semibold text-break fs-6">
                                  {product.display_name}
                                </div>
                                {product.description && (
                                  <div className="small text-muted text-break mt-1">
                                    {product.description.length > 50 
                                      ? `${product.description.substring(0, 50)}...` 
                                      : product.description
                                    }
                                  </div>
                                )}
                              </div>
                              <div className="d-flex flex-wrap align-items-start gap-1">
                                {product.category && (
                                  <Badge bg="secondary" className="flex-shrink-0 small">
                                    {product.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div className="p-4 text-center text-muted">
                        <div className="small mb-2">
                          {productSearchTerm.trim() 
                            ? 'No products found' 
                            : 'Start typing to search products'
                          }
                        </div>
                      </div>
                    )}
                    {selectedProductData && (
                      <div className="border-top p-3">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedProductData(null);
                            setSelectedProduct('');
                            setProductSearchTerm('');
                            setShowProductDropdown(false);
                          }}
                          className="w-100"
                        >
                          Cancel Selection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedProductData && (
                <div className="mt-2 p-3 bg-light rounded border">
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <small className="text-muted">Selected: </small>
                      <span className="fw-semibold text-break">{selectedProductData.display_name}</span>
                    </div>
                    {selectedProductData.description && (
                      <div className="small text-muted text-break">
                        {selectedProductData.description}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Form.Group>

            {!productHasTypes ? (
              <Form.Group controlId="salePrice" className="mb-3">
                <Form.Label>Sale Price</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  placeholder="Enter sale price" 
                  value={salePrice} 
                  onChange={(e) => setSalePrice(e.target.value)} 
                  required 
                  disabled={submitting || !canEdit}
                  className="w-100"
                  style={{ fontSize: '16px' }}
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Stock, price, and deals by product type</Form.Label>
                <small className="text-muted d-block mb-2">
                  Each type has its own quantity and optional bulk deals. Total quantity is stored as the sum of all types.
                </small>
                {selectedProductData.product_types.map((t) => (
                  <div key={t.id} className="border rounded p-2 mb-3">
                    <Form.Check
                      type="checkbox"
                      id={`edit-inv-type-${t.id}`}
                      label={<span className="fw-semibold">{t.name || t.id}</span>}
                      checked={!!typeSelections[t.id]}
                      disabled={submitting || !canEdit}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setTypeSelections((prev) => ({ ...prev, [t.id]: on }));
                        if (!on) {
                          setTypePriceInputs((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeQuantityInputs((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeDealsRecord((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                          setTypeDealDrafts((prev) => {
                            const next = { ...prev };
                            delete next[t.id];
                            return next;
                          });
                        }
                      }}
                    />
                    {typeSelections[t.id] && (
                      <div className="mt-2 ps-1">
                        <Row className="g-2 mb-2">
                          <Col xs={12} sm={6}>
                            <Form.Label className="small mb-1">Unit price</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={typePriceInputs[t.id] || ''}
                              onChange={(e) => setTypePriceInputs((prev) => ({ ...prev, [t.id]: e.target.value }))}
                              disabled={submitting || !canEdit}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Label className="small mb-1">Quantity in stock</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              step="1"
                              placeholder="0"
                              value={typeQuantityInputs[t.id] ?? ''}
                              onChange={(e) => setTypeQuantityInputs((prev) => ({ ...prev, [t.id]: e.target.value }))}
                              disabled={submitting || !canEdit}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                        </Row>
                        <div className="small text-muted mb-1">Bulk deals for this type (optional)</div>
                        <Row className="g-2 align-items-end mb-2">
                          <Col xs={6} md={4}>
                            <Form.Control
                              type="number"
                              step="1"
                              placeholder="Deal qty"
                              value={getTypeDealDraft(t.id).q}
                              onChange={(e) => setTypeDealDraftField(t.id, 'q', e.target.value)}
                              disabled={submitting || !canEdit}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={6} md={4}>
                            <Form.Control
                              type="number"
                              step="0.01"
                              placeholder="Deal price"
                              value={getTypeDealDraft(t.id).p}
                              onChange={(e) => setTypeDealDraftField(t.id, 'p', e.target.value)}
                              disabled={submitting || !canEdit}
                              style={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col xs={12} md={4}>
                            <Button type="button" variant="outline-primary" size="sm" className="w-100" disabled={submitting || !canEdit} onClick={() => addDiscountDealForType(t.id)}>
                              Add deal
                            </Button>
                          </Col>
                        </Row>
                        {(typeDealsRecord[t.id] || []).length > 0 && (
                          <ListGroup variant="flush" className="small">
                            {(typeDealsRecord[t.id] || []).map((deal) => {
                              const unit = unitPriceForType(t.id);
                              const regularPrice = (unit != null ? unit : 0) * deal.quantity;
                              const savings = regularPrice - deal.price;
                              return (
                                <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center px-0 py-2">
                                  <span>
                                    <strong>{deal.quantity}</strong> for <strong>${deal.price.toFixed(2)}</strong>
                                    <span className="text-success ms-1">(save ${savings.toFixed(2)})</span>
                                  </span>
                                  <Button variant="outline-danger" size="sm" disabled={submitting || !canEdit} onClick={() => removeDiscountDealForType(t.id, deal.id)}>
                                    Remove
                                  </Button>
                                </ListGroup.Item>
                              );
                            })}
                          </ListGroup>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </Form.Group>
            )}

            {!productHasTypes && (
            <Form.Group controlId="quantity" className="mb-3">
              <Form.Label>Quantity</Form.Label>
              <Form.Control 
                type="number" 
                step="1" 
                placeholder="Enter quantity" 
                value={quantity} 
                onChange={(e) => setQuantity(e.target.value)} 
                required 
                disabled={submitting || !canEdit}
                className="w-100"
                style={{ fontSize: '16px' }}
              />
            </Form.Group>
            )}

            {!productHasTypes && (
            <Form.Group controlId="discountDeals" className="mb-3">
              <Form.Label>Discount Deals (Optional)</Form.Label>
              <small className="text-muted d-block mb-2">
                Create bulk purchase discounts. For example: Buy 3 items for $8.00 instead of $12.00
              </small>
              <Card className="mb-3">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Quantity for deal"
                        value={dealQuantity}
                        onChange={(e) => setDealQuantity(e.target.value)}
                        disabled={submitting || !canEdit}
                        className="w-100"
                        style={{ fontSize: '16px' }}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Price for deal"
                        value={dealPrice}
                        onChange={(e) => setDealPrice(e.target.value)}
                        disabled={submitting || !canEdit}
                        className="w-100"
                        style={{ fontSize: '16px' }}
                      />
                    </Col>
                  </Row>
                  <Button
                    variant="outline-primary"
                    onClick={addDiscountDeal}
                    className="mt-2"
                    disabled={!dealQuantity || !dealPrice || submitting || !canEdit}
                  >
                    Add Deal
                  </Button>
                </Card.Body>
              </Card>

              {discountDeals.length > 0 && (
                <div className="mb-3">
                  <h6>Current Discount Deals:</h6>
                  <ListGroup>
                    {discountDeals.map((deal) => {
                      const unit = getFormBaselineUnitPrice();
                      const regularPrice = (unit != null ? unit : 0) * deal.quantity;
                      const savings = regularPrice - deal.price;
                      return (
                        <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                            <br />
                            <small className="text-success">
                              Save ${savings.toFixed(2)} (${(deal.price / deal.quantity).toFixed(2)} per item)
                            </small>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeDiscountDeal(deal.id)}
                            disabled={submitting || !canEdit}
                          >
                            Remove
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </Form.Group>
            )}

            <Button 
              type="submit" 
              variant="primary" 
              className="w-100 mt-4 mb-5 py-3"
              disabled={submitting || !canEdit || (userRole === 'Employee' && !employeeSupermarketId)}
              style={{ fontSize: '16px' }}
            >
              {submitting ? (
                <div className="d-flex align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating...
                </div>
              ) : (
                'Update Inventory Item'
              )}
            </Button>
          </Form>
        )}
      </Container>
    </Layout>
  );
};

export default EditInventoryItem;
